import { useSelect, UseSelectGetItemPropsOptions, UseSelectStateChangeTypes } from "downshift";
import { ListboxOption, ListboxOptionsGroup, OptionsGroup, SelectOption } from "./select.types";

export const isListOfOptions = (options: any): options is SelectOption[] =>
  options && options[0] && options[0].value !== undefined;

export const isListOfOptionsGroups = <S>(options: any): options is OptionsGroup<S>[] =>
  options && options[0] && options[0].groupLabel !== undefined;

export const filterOptions = <S extends SelectOption>(options: S[], selectedItem: S[]) => {
  return options.filter((option) => !selectedItem.some(({ value }: any) => value === option.value));
};

export const filterOptionsGroups = <S extends SelectOption>(
  groupedOptions: OptionsGroup<S>[],
  selectedItem: OptionsGroup<S>[]
) => {
  return groupedOptions
    .map((group) => {
      return {
        groupLabel: group.groupLabel,
        groupOptions: group.groupOptions.filter(
          (option) => !selectedItem.some(({ value }: any) => value === option.value)
        ),
      };
    })
    .filter((group) => group.groupOptions.length > 0);
};

export const flattenOptions = <S extends SelectOption>(filteredOptions: OptionsGroup<S>[]) => {
  return [].concat.apply(
    [],
    filteredOptions.map((o) => o.groupOptions)
  ) as S[];
};

export const defaultOptionToString = <Option extends ListboxOption = ListboxOption>(
  option: Option | null | undefined
) => option.value;

export const LABEL_ID_SUFFIX = "-label";

export const TOGGLE_BUTTON_ID_SUFFIX = "-toggle-button";

export const LISTBOX_ID_SUFFIX = "-listbox";

export const INPUT_ID_SUFFIX = "-input";

export const STATE_CHANGE_TYPES_FOR_SELECTION_FOLLOWS_FOCUS: UseSelectStateChangeTypes[] = [
  useSelect.stateChangeTypes.ToggleButtonKeyDownArrowDown,
  useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp,
  useSelect.stateChangeTypes.MenuKeyDownArrowDown,
  useSelect.stateChangeTypes.MenuKeyDownArrowUp,
  useSelect.stateChangeTypes.MenuKeyDownHome,
  useSelect.stateChangeTypes.MenuKeyDownEnd,
  useSelect.stateChangeTypes.MenuKeyDownCharacter,
];

export const getOptionPropGetterOptions = <Option extends ListboxOption>(
  option: Option,
  index: number,
  selected: boolean
) => {
  const itemPropGetterOptions: UseSelectGetItemPropsOptions<Option> = {
    item: option,
    index,
    disabled: !!option.disabled,
    "aria-selected": selected ? "true" : "false",
  };
  if (option.id) {
    itemPropGetterOptions.id = option.id;
  }
  return itemPropGetterOptions;
};

export const transformFlatOptionsToGroupedOptions = <Option extends ListboxOption = ListboxOption>(
  options: Option[]
): ListboxOptionsGroup<Option>[] => {
  const unknownGroupName = "Unknown";
  const transformedOptions: ListboxOptionsGroup<Option>[] = [];

  options.reduce((t, o) => {
    if (t.length === 0 || t[t.length - 1].name !== (o.group ?? unknownGroupName)) {
      t.push({
        name: o.group ?? unknownGroupName,
        options: [o],
      });
    } else {
      t[t.length - 1].options.push(o);
    }
    return t;
  }, transformedOptions);

  return transformedOptions;
};
